import type { ProjectKind, Sample } from '../data/types';

/**
 * Build a kid-friendly per-class download zip.
 *
 * Each sample becomes a file inside the zip — `001.png` for image
 * samples, `001.wav` for audio. The kid can open the zip with no extra
 * tooling on macOS, Windows, or a Chromebook. Pure: no DOM, no DB.
 */
export async function packClassSamples(
  _className: string,
  kind: ProjectKind,
  samples: Sample[],
): Promise<Blob> {
  const { zipSync } = await import('fflate');
  const ext = defaultExtensionFor(kind);
  const files: Record<string, Uint8Array> = {};
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i]!;
    const buf = await sample.blob.arrayBuffer();
    const filename = `${String(i + 1).padStart(3, '0')}${extensionFromBlob(sample.blob, ext)}`;
    files[filename] = new Uint8Array(buf);
  }
  const zipped = zipSync(files);
  const copy = new Uint8Array(zipped.byteLength);
  copy.set(zipped);
  return new Blob([copy], { type: 'application/zip' });
}

function defaultExtensionFor(kind: ProjectKind): string {
  return kind === 'audio' ? '.wav' : '.png';
}

function extensionFromBlob(blob: Blob, fallback: string): string {
  if (blob.type === 'image/png') return '.png';
  if (blob.type === 'image/jpeg') return '.jpg';
  if (blob.type === 'image/webp') return '.webp';
  if (blob.type === 'audio/wav' || blob.type === 'audio/x-wav') return '.wav';
  if (blob.type === 'audio/webm') return '.webm';
  if (blob.type === 'audio/mpeg') return '.mp3';
  return fallback;
}

/** Build a slugified zip filename from a class name. */
export function buildClassZipFilename(className: string): string {
  const slug = className
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const stem = slug || 'class';
  return `${stem}-samples.zip`;
}
